import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Camera, FileSignature, ClipboardCheck, Upload, Eye, Download, Trash2, FileText, Lock, PackageCheck, FolderOpen, Archive, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { usePlantillasDocumentos, PLANTILLA_CATEGORIAS, type PlantillaCategoria } from "@/hooks/usePlantillasDocumentos";
import { downloadAsZip, downloadSingle } from "@/lib/zipDownload";

interface EntregaDoc {
  id: string;
  obra_id: string;
  empresa_id: string;
  tipo: 'foto' | 'certificado' | 'checklist' | 'otro';
  nombre_archivo: string;
  url_archivo: string;
  descripcion: string | null;
  tamano_archivo: number | null;
  subido_por: string;
  created_at: string;
}

interface Props {
  obraId: string;
  obraEstado: string;
  hideHeader?: boolean;
}

const SECCIONES: Array<{
  tipo: EntregaDoc['tipo'];
  titulo: string;
  descripcion: string;
  icon: typeof Camera;
  accept: string;
  multiple: boolean;
}> = [
  { tipo: 'foto', titulo: 'Fotos de entrega', descripcion: 'Imágenes finales del estado de la obra entregada. Puedes seleccionar varias a la vez.', icon: Camera, accept: 'image/*', multiple: true },
  { tipo: 'certificado', titulo: 'Certificados', descripcion: 'Certificados de instalación, garantía o conformidad.', icon: FileSignature, accept: 'application/pdf,image/*', multiple: false },
  { tipo: 'checklist', titulo: 'Checklist de entrega', descripcion: 'Listas de verificación y actas firmadas con el cliente.', icon: ClipboardCheck, accept: 'application/pdf,image/*', multiple: false },
];

const BUCKET = 'obra-entrega-documentos';

export function ObraEntregaTab({ obraId, obraEstado, hideHeader = false }: Props) {
  const { perfil, user } = useAuth();
  const { toast } = useToast();
  const isAdmin = perfil?.rol === 'admin' || perfil?.rol === 'gestor';
  const finalizada = obraEstado === 'finalizada';
  const canUpload = isAdmin && finalizada;

  const [docs, setDocs] = useState<EntregaDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingTipo, setUploadingTipo] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EntregaDoc | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [zipping, setZipping] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from('obra_entrega_documentos')
      .select('*')
      .eq('obra_id', obraId)
      .order('created_at', { ascending: false });
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      setDocs((data ?? []) as EntregaDoc[]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [obraId]);

  const handleUploadFiles = async (tipo: EntregaDoc['tipo'], files: File[]) => {
    if (!perfil?.empresa_id || !user?.id || files.length === 0) return;
    setUploadingTipo(tipo);
    setUploadProgress({ current: 0, total: files.length });
    let ok = 0;
    let fail = 0;
    try {
      const { compressImageForUpload } = await import('@/lib/imageUtils');
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress({ current: i + 1, total: files.length });
        try {
          // Higher-quality compression for fotos de entrega; skip for non-images
          const upFile = tipo === 'foto'
            ? await compressImageForUpload(file, { maxDimension: 2200, quality: 0.92 })
            : file;
          const safeName = upFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
          const path = `${perfil.empresa_id}/${obraId}/${tipo}/${Date.now()}_${i}_${safeName}`;
          const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, upFile);
          if (upErr) throw upErr;
          const { error: insErr } = await (supabase as any).from('obra_entrega_documentos').insert({
            obra_id: obraId,
            empresa_id: perfil.empresa_id,
            tipo,
            nombre_archivo: file.name,
            url_archivo: path,
            tamano_archivo: upFile.size,
            subido_por: user.id,
          });
          if (insErr) throw insErr;
          ok++;
        } catch (e: any) {
          fail++;
          console.error('upload fail', file.name, e);
        }
      }
      if (ok > 0) toast({ title: `${ok} archivo${ok > 1 ? 's' : ''} subido${ok > 1 ? 's' : ''}`, description: fail > 0 ? `${fail} fallaron` : undefined });
      if (ok === 0 && fail > 0) toast({ title: 'Error al subir', description: `${fail} archivos fallaron`, variant: 'destructive' });
      await load();
    } finally {
      setUploadingTipo(null);
      setUploadProgress(null);
    }
  };

  const handleView = async (doc: EntregaDoc) => {
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(doc.url_archivo, 3600);
    if (error || !data) return toast({ title: 'Error', description: error?.message, variant: 'destructive' });
    window.open(data.signedUrl, '_blank');
  };

  const handleDownloadOne = async (doc: EntregaDoc) => {
    try {
      await downloadSingle(BUCKET, doc.url_archivo, doc.nombre_archivo);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await supabase.storage.from(BUCKET).remove([deleteTarget.url_archivo]);
      const { error } = await (supabase as any).from('obra_entrega_documentos').delete().eq('id', deleteTarget.id);
      if (error) throw error;
      toast({ title: 'Documento eliminado' });
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const docsByTipo = (tipo: string) => docs.filter(d => d.tipo === tipo);

  const toggleSelect = (id: string) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const clearSelection = (tipo?: string) => {
    if (!tipo) return setSelected({});
    setSelected(s => {
      const next = { ...s };
      docsByTipo(tipo).forEach(d => { delete next[d.id]; });
      return next;
    });
  };
  const selectAll = (tipo: string) => {
    setSelected(s => {
      const next = { ...s };
      docsByTipo(tipo).forEach(d => { next[d.id] = true; });
      return next;
    });
  };

  const downloadZip = async (items: EntregaDoc[], zipName: string) => {
    if (items.length === 0) return;
    setZipping(true);
    try {
      await downloadAsZip(BUCKET, items.map(d => ({ name: d.nombre_archivo, bucketPath: d.url_archivo })), zipName);
    } catch (e: any) {
      toast({ title: 'Error al generar ZIP', description: e.message, variant: 'destructive' });
    } finally {
      setZipping(false);
    }
  };

  const { plantillas, loading: loadingPlantillas, download: downloadPlantilla } = usePlantillasDocumentos();
  const plantillasByCategoria = (cat: PlantillaCategoria) => plantillas.filter(p => p.categoria === cat);

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {!hideHeader && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PackageCheck className="h-5 w-5 text-primary" />
                Documentación de entrega
              </CardTitle>
              <CardDescription>
                {finalizada
                  ? 'Sube las fotos finales, certificados y checklists firmados que documentan la entrega de la obra al cliente.'
                  : 'La subida de documentación de entrega estará disponible cuando la obra se marque como finalizada.'}
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
              Plantillas disponibles
              {plantillas.length > 0 && <Badge variant="secondary">{plantillas.length}</Badge>}
            </CardTitle>
            <CardDescription>
              Descarga las plantillas de tu empresa para preparar la documentación de entrega.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingPlantillas ? (
              <p className="text-sm text-muted-foreground">Cargando plantillas...</p>
            ) : plantillas.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                No hay plantillas. {isAdmin && 'Añádelas desde Ajustes → Plantillas de entrega.'}
              </p>
            ) : (
              <div className="space-y-3">
                {PLANTILLA_CATEGORIAS.map(({ value, label }) => {
                  const items = plantillasByCategoria(value);
                  if (items.length === 0) return null;
                  return (
                    <div key={value}>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
                      <div className="space-y-1.5">
                        {items.map(p => (
                          <div key={p.id} className="flex items-center justify-between gap-2 p-2 rounded-md border bg-muted/30">
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{p.nombre}</p>
                                {(p.etiqueta || p.descripcion) && (
                                  <p className="text-xs text-muted-foreground truncate">
                                    {p.etiqueta}{p.etiqueta && p.descripcion ? ' · ' : ''}{p.descripcion}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => downloadPlantilla(p)}>
                              <Download className="h-4 w-4 mr-1" />
                              Descargar
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {SECCIONES.map(({ tipo, titulo, descripcion, icon: Icon, accept, multiple }) => {
          const items = docsByTipo(tipo);
          const inputId = `entrega-upload-${tipo}`;
          const selectedItems = items.filter(d => selected[d.id]);
          const allSelected = items.length > 0 && selectedItems.length === items.length;
          const isUploadingThis = uploadingTipo === tipo;

          return (
            <Card key={tipo}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      {titulo}
                      {items.length > 0 && <Badge variant="secondary">{items.length}</Badge>}
                    </CardTitle>
                    <CardDescription className="mt-1">{descripcion}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {items.length > 1 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={zipping}
                        onClick={() => downloadZip(items, `${titulo.toLowerCase().replace(/\s+/g, '-')}.zip`)}
                      >
                        <Archive className="h-4 w-4 mr-1" />
                        {zipping ? 'Generando...' : 'Descargar todo (ZIP)'}
                      </Button>
                    )}
                    {isAdmin && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <input
                              id={inputId}
                              type="file"
                              accept={accept}
                              multiple={multiple}
                              className="hidden"
                              disabled={!canUpload || isUploadingThis}
                              onChange={(e) => {
                                const files = Array.from(e.target.files ?? []);
                                if (files.length) handleUploadFiles(tipo, files);
                                e.target.value = '';
                              }}
                            />
                            <Button
                              type="button"
                              size="sm"
                              disabled={!canUpload || isUploadingThis}
                              onClick={() => document.getElementById(inputId)?.click()}
                            >
                              {canUpload ? <Upload className="h-4 w-4 mr-1" /> : <Lock className="h-4 w-4 mr-1" />}
                              {isUploadingThis
                                ? `Subiendo ${uploadProgress?.current}/${uploadProgress?.total}...`
                                : multiple ? 'Subir fotos' : 'Subir'}
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {!canUpload && (
                          <TooltipContent>Disponible cuando la obra esté finalizada</TooltipContent>
                        )}
                      </Tooltip>
                    )}
                  </div>
                </div>

                {selectedItems.length > 0 && (
                  <div className="mt-3 flex items-center justify-between gap-2 p-2 rounded-md bg-primary/5 border border-primary/20">
                    <span className="text-sm font-medium">{selectedItems.length} seleccionado{selectedItems.length > 1 ? 's' : ''}</span>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" disabled={zipping} onClick={() => downloadZip(selectedItems, `${titulo.toLowerCase().replace(/\s+/g, '-')}-seleccion.zip`)}>
                        <Archive className="h-4 w-4 mr-1" />
                        Descargar ZIP
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => clearSelection(tipo)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-sm text-muted-foreground">Cargando...</p>
                ) : items.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">Sin documentos.</p>
                ) : (
                  <div className="space-y-2">
                    {items.length > 1 && (
                      <div className="flex items-center gap-2 px-2 pb-1">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={(v) => v ? selectAll(tipo) : clearSelection(tipo)}
                        />
                        <span className="text-xs text-muted-foreground">Seleccionar todos</span>
                      </div>
                    )}
                    {items.map((d) => (
                      <div key={d.id} className="flex items-center justify-between gap-2 p-2 rounded-md border bg-muted/30">
                        <div className="flex items-center gap-2 min-w-0">
                          <Checkbox
                            checked={!!selected[d.id]}
                            onCheckedChange={() => toggleSelect(d.id)}
                          />
                          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{d.nombre_archivo}</p>
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(d.created_at), "dd/MM/yyyy HH:mm", { locale: es })}
                              {d.tamano_archivo ? ` · ${(d.tamano_archivo / 1024).toFixed(0)} KB` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="icon" variant="ghost" onClick={() => handleView(d)} title="Ver">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleDownloadOne(d)} title="Descargar">
                            <Download className="h-4 w-4" />
                          </Button>
                          {isAdmin && (
                            <Button size="icon" variant="ghost" onClick={() => setDeleteTarget(d)} title="Eliminar">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
              <AlertDialogDescription>
                Se eliminará "{deleteTarget?.nombre_archivo}" permanentemente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}
